import React, { useEffect, useState } from "react";
import { Form, Input, Select, DatePicker, TimePicker } from 'antd';
import FormItem from "components/fields/FormItem";
import { connect, useSelector } from "react-redux";
import { 
	getLeaveDetails, 
	doLeaveCreate, 
	getScheduledOutTime,
	doLeaveUpdate,
} from "../../../../redux/leave/leaveState";
import CrudButtons from "components/button/crudbuttons";
import { DATE_FORMAT, MODULE_LEAVE, ROLE_SUPER_ADMIN, TIME_FORMAT } from "variables/constants";
import { requiredField } from "utils/crudSagaHelper";
import SwitchField from "components/fields/SwitchField";
import TextArea from "antd/es/input/TextArea";
import dayjs from "dayjs";

const LeaveForm = ({
	getRecord, 
	doCreate, 
	doUpdate, 
	recordData, 
	requesting, 
	success, 
	onCancel,
	submitLabel,
	getSchedOutTime,
	scheduledOutTime,
	...props
}) => {
	
  	const { recordId, mode } = props;  	
  	const [form] = Form.useForm();
	const authUser = useSelector((state) => state.auth.user);
	const { RangePicker } = DatePicker;
	const [formData, setFormData] = useState({
		lve_user_id: null,
		lve_date_range: [],
		lve_is_undertime: false,
		lve_undertime_from: null,
		lve_undertime_to: null,
	});
	const [computedDays, setComputedDays] = useState('');
	const [computedHours, setComputedHours] = useState('');

	const isUndertime = Form.useWatch("lve_is_undertime", form);
	
  	useEffect(() => {
        getRecord(recordId);
	}, [getRecord, recordId]);

	useEffect(() => {
		if (!recordData?.lve_id) {
			form.resetFields();

			setComputedDays("");
			setComputedHours("");

			setFormData({
				lve_user_id: null,
				lve_date_range: [],
				lve_is_undertime: false,
				lve_undertime_from: null,
				lve_undertime_to: null,
			});

			return;
		}

		const dateRange = [
			dayjs(recordData.lve_date_from),
			dayjs(recordData.lve_date_to),
		];

		const undertimeFrom = recordData.lve_undertime_from
			? dayjs(recordData.lve_undertime_from, TIME_FORMAT)
			: null;

		const undertimeTo = recordData.lve_undertime_to
			? dayjs(recordData.lve_undertime_to, TIME_FORMAT)
			: null;

		form.setFieldsValue({
			...recordData,
			lve_is_undertime: !!recordData.lve_is_undertime,
			lve_is_unpaid: !!recordData.lve_is_unpaid,
			lve_date_range: dateRange,
			lve_undertime_from: undertimeFrom,
			lve_undertime_to: undertimeTo,
		});

		setComputedDays(dateRange[1].diff(dateRange[0], "day") + 1);

		if (undertimeFrom && undertimeTo) {
			calculateUndertimeHours(undertimeFrom, undertimeTo);
		}
	}, [recordData]);

	useEffect(() => {
		if (scheduledOutTime?.scheduled_time_out) {
			const scheduled = dayjs(
				scheduledOutTime.scheduled_time_out,
				TIME_FORMAT
			);
			setFormData(prev => ({
				...prev,
				lve_undertime_from: scheduled,
			}));
			form.setFieldsValue({
				lve_undertime_from: scheduled,
			});
		}
	}, [scheduledOutTime]);
    
  	const onSubmit = (values) => {
		try {
			const payload = {
				...values,
				lve_id: recordData?.lve_id,
				lve_date_from: values.lve_date_range?.[0]?.format(DATE_FORMAT),
				lve_date_to: values.lve_date_range?.[1]?.format(DATE_FORMAT),
			};

			delete payload.lve_date_range;

			payload.lve_is_undertime = payload.lve_is_undertime ? 1 : 0;
			payload.lve_is_unpaid = payload.lve_is_unpaid ? 1 : 0;

			if (!payload.lve_user_id) {
				payload.lve_user_id = authUser?.id;
			}

			if (payload.lve_is_undertime) {
				payload.lve_undertime_from =
					payload.lve_undertime_from?.format(TIME_FORMAT);

				payload.lve_undertime_to =
					payload.lve_undertime_to?.format(TIME_FORMAT);
			}
			recordData?.lve_id > 0
				? doUpdate(payload)
				: doCreate(payload);

		} catch(error) {
			console.error("Save failed:", error);
		}
	};

	const calculateUndertimeHours = (scheduledOut, actualOut) => {
		if (!scheduledOut || !actualOut) {
			setComputedHours(0);
			return;
		}

		const minutes = scheduledOut.diff(actualOut, "minute");

		setComputedHours(
			Math.max(0, Number((minutes / 60).toFixed(2)))
		);
	};
  
  	return (
		
    	<Form form={form}
			className={mode !== "view" ? "form" : ""}
			layout="vertical"
			onFinish={onSubmit}
		>   
			<FormItem
				name="lve_id"
				hidden
			>
				<Input type="hidden"/>
			</FormItem>

			<FormItem
				name="lve_status_id"
				hidden
			>
				<Input type="hidden"/>
			</FormItem>

			<>
				<div
					className={`grid gap-4 ${
					authUser?.user_level === ROLE_SUPER_ADMIN
						? "grid-cols-1 lg:grid-cols-3"
						: "grid-cols-1 lg:grid-cols-2"
					}`}
				>     		            	            
					{authUser?.user_level === ROLE_SUPER_ADMIN && mode !== "view" && (
						<FormItem
							className="font-medium mb-2"
							label="Employee"
							required
						>
							<FormItem
								name="lve_user_id"
								noStyle
								rules={[requiredField]}
								initialValue={formData?.lve_user_id ?? recordData?.lve_user_id ?? null}
							>
								<Select
									showSearch
									allowClear
									className="input-field"
									placeholder="Select Employee"
									onChange={(value) =>
										setFormData((prev) => ({ ...prev, lve_user_id: value }))
									}
									value={formData?.lve_user_id ?? recordData?.lve_user_id ?? undefined}
									options={recordData?.employeeList}
									optionFilterProp="label"
								/>
							</FormItem>
						</FormItem>
					)}  
					
					<FormItem className="font-medium mb-2"
						name="lve_type_id" 
						label="Leave Type"
						rules={[requiredField]}
					>
						<Select id="lve_type_id"
							allowClear={true}
							showSearch
							className="input-field"
							options={recordData?.leaveTypeList}
							placeholder="Select leave type"
						/>
					</FormItem>

					<FormItem
						className="font-medium mb-2"
						name="lve_date_range" 
						label="Date Range"
						rules={[requiredField]}
					>
						<RangePicker
							className="input-field w-full"
							format={DATE_FORMAT}
							onChange={(dates) => {
								setFormData((prev) => ({
									...prev,
									lve_date_range: dates,
								}));

								if (dates?.[0] && authUser?.id) {
										setComputedHours(0);

										setFormData((prev) => ({
											...prev,
											lve_date_range: dates,
											lve_undertime_from: null,
											lve_undertime_to: null,
										}));

										getSchedOutTime({
											userId:
												authUser?.user_level === ROLE_SUPER_ADMIN
													? form.getFieldValue("lve_user_id")
													: authUser.id,
											date: dates[0].format(DATE_FORMAT),
										});
									}

									if (!dates || dates.length !== 2) {
										setComputedDays(0);
										return;
									}

									const days = dates[1].diff(dates[0], "day") + 1;
									setComputedDays(days);
								}}
							placeholder={["Start date", "End date"]}
						/>
					</FormItem>
				</div>
				<div>
					<div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
						{!formData.lve_is_undertime ? (
							<FormItem
								label="Count"
								className="font-medium mb-2"
							>
								<Input
									value={computedDays}
									readOnly
									className="input-field"
									placeholder="0"
								/>
							</FormItem>
						) : (
							<FormItem
								label="Undertime Hours"
								className="font-medium mb-2"
							>
								<Input
									value={`${computedHours} hour(s)`}
									readOnly
									className="input-field"
									placeholder="0 hour(s)"
								/>
							</FormItem>
						)}

						<div className="grid grid-cols-2 gap-4 md:contents">
							<FormItem
								label="Is Undertime"
								name="lve_is_undertime"
								className="font-medium mb-2"
							>
								<SwitchField
									id="lve_is_undertime"
									mode="edit"
									type="text"
									labelYes="Yes"
									labelNo="No"
									onChange={(value) => {
										setFormData((prev) => ({
											...prev,
											lve_is_undertime: value,
										}));

										if (value) {
											if (computedDays > 1) {
												const singleDate = formData.lve_date_range?.[0];

												if (singleDate) {
													form.setFieldsValue({
														lve_date_range: [singleDate, singleDate],
													});
													setComputedDays(1);
												}
											}
										} else {
											setComputedHours(0);
											form.setFieldsValue({
												lve_undertime_from: null,
												lve_undertime_to: null,
											});
										}
									}}
								/>
							</FormItem>

							<FormItem
								label="Is Unpaid"
								name="lve_is_unpaid"
								className="font-medium mb-2"
							>
								<SwitchField
									id="lve_is_unpaid"
									mode="edit"
									type="text"
									labelYes="Yes"
									labelNo="No"
								/>
							</FormItem>
						</div>
					</div>

					{isUndertime && (
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<FormItem
								name="lve_undertime_from"
								label="Scheduled Time Out"
							>
								<TimePicker
									className="input-field w-full"
									format="HH:mm"
									value={formData.lve_undertime_from}
									disabled
								/>
							</FormItem>

							<FormItem
								name="lve_undertime_to"
								label="Actual Time Out"
								rules={[requiredField]}
							>
								<TimePicker
									className="input-field w-full"
									format="HH:mm"
									value={formData.lve_undertime_to}
									disabled={!scheduledOutTime?.scheduled_time_out}
									onChange={(time) => {

										setFormData((prev) => ({
											...prev,
											lve_undertime_to: time,
										}));

										calculateUndertimeHours(
											formData.lve_undertime_from,
											time
										);
									}}
								/>
							</FormItem>
						</div>
					)}
					
					<FormItem
						className="font-medium mb-2 md:col-span-3"
						name="lve_reason"
						label="Reason"
						rules={[requiredField]}
					>
						<TextArea
							id="lve_reason"
							className="input-field"
							maxLength={100}
							placeholder={formData.lve_is_undertime ? "Please provide reason for undertime..." : "Please provide reason for leave..."}
						/>
					</FormItem>
				</div>
			</>
			
			<div className="mt-4">
				{(mode === "edit") && (
					<CrudButtons 
						mode={mode} 
						recordId={recordData?.lve_id ?? 0} 
						isModal={true}
						modalId={recordData?.lve_id > 0 ? 'edit_leave' : 'add_leave'}
						cancelLabel="Cancel"
						doHideModal={onCancel}
						submitLabel="Submit"
						moduleId={MODULE_LEAVE}
					/>
				)}
			</div> 				 			
		</Form>	 	
  	);
};

const mapStateToProps = (state) => ({
  	recordData: state.leave.leaveDetails?.data,
  	requesting: state.leave.leaveDetails?.requesting,
  	success: state.leave.leaveDetails?.success,

	scheduledOutTime: state.leave.scheduledOutTime?.data,
});

const mapDispatchToProps = (dispatch) => ({
	getRecord: (id) => dispatch(getLeaveDetails(id)),
  	doCreate: (data) => dispatch(doLeaveCreate(data)),
	doUpdate: (data) => dispatch(doLeaveUpdate(data)),
	getSchedOutTime: (data) => dispatch(getScheduledOutTime(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(LeaveForm);